import { useEffect, useRef, useState } from 'react'
import { RefreshCw, Trash2, Upload } from 'lucide-react'
import { fileCategories, fileCategory, type FileCategory } from './fileCategories'
import GuestMediaViewer from './GuestMediaViewer'

type Storage = { role: string; quota: { bytes: number; used: number }; storage: Record<string, { resource_id: string }> }
type Entry = { name: string; size: number; is_dir: boolean; resource_id: string }
const sizeLabel = (bytes: number) => `${(bytes / 1024 ** 3).toFixed(2)} GB`
const fileSizeLabel = (bytes: number) => bytes < 1024 ** 2 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : bytes < 1024 ** 3 ? `${(bytes / 1024 ** 2).toFixed(1)} MB` : sizeLabel(bytes)
async function cloudFetch(url: string, options: RequestInit, timeout = 20000) {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeout)
  try { return await fetch(url, { ...options, signal: controller.signal }) }
  finally { window.clearTimeout(timer) }
}

export default function GuestCloud({ lease, email, active = true }: { lease: string; email: string; active?: boolean }) {
  const [storage, setStorage] = useState<Storage | null>(null)
  const [files, setFiles] = useState<Entry[]>([])
  const [category, setCategory] = useState<FileCategory>('Todos')
  const [pendingDelete, setPendingDelete] = useState<Entry | null>(null)
  const [viewer, setViewer] = useState<{ items: Entry[]; selectedId: string } | null>(null)
  const [status, setStatus] = useState('')
  const [busy, setBusy] = useState(false)
  const [checking, setChecking] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [lastUpdated, setLastUpdated] = useState('')
  const refreshing = useRef<Promise<Storage> | null>(null)
  const [batch, setBatch] = useState<{ name: string; result: string }[]>([])
  const uploading = useRef(false)
  const renewal = useRef<Promise<void> | null>(null)
  const activeLease = useRef(lease)
  const currentLease = () => {
    if ((localStorage.getItem('NEXUS_SESSION_EMAIL') || '').toLowerCase() === email.toLowerCase()) {
      const stored = localStorage.getItem('NEXUS_SESSION_LEASE')
      if (stored) activeLease.current = stored
    }
    return activeLease.current
  }
  const headers = () => ({ Authorization: `Bearer ${currentLease()}` })
  useEffect(() => {
    const guard = (event: Event) => { if (busy || uploading.current) event.preventDefault() }
    window.addEventListener('nexus-before-update', guard)
    return () => window.removeEventListener('nexus-before-update', guard)
  }, [busy])
  useEffect(() => {
    activeLease.current = lease
    let renewing = false
    let stopped = false
    const renew = () => {
      if (renewing || uploading.current || stopped || document.visibilityState === 'hidden') return
      renewing = true
      renewal.current = (async () => {
      const check = async () => {
        const original = currentLease()
        const checked = await cloudFetch('/vault/auth/introspect_session', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_lease: original })
        })
        const identity = await checked.json()
        if (!checked.ok || !identity.ok) throw new Error('No se pudo validar la sesión de archivos.')
        const expires = Number(identity.actor?.session_expires_at)
        if (!Number.isFinite(expires) || expires * 1000 - Date.now() > 3 * 86400000) return
        if (currentLease() !== original) return
        const response = await cloudFetch('/vault/auth/renew_session', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_lease: original })
        })
        const result = await response.json()
        if (response.ok && result.ok && result.session_lease && currentLease() === original) {
          activeLease.current = result.session_lease
          localStorage.setItem('NEXUS_SESSION_LEASE', result.session_lease)
        }
      }
      try {
        if (navigator.locks) await navigator.locks.request('nexus-tablet-session-renewal', check)
        else await check()
      } catch { /* A transient network failure is retried on the next interval. */ }
      finally { renewing = false }
      })()
      return renewal.current
    }
    const timer = window.setInterval(() => void renew(), 120000)
    const onVisible = () => { if (document.visibilityState === 'visible') void renew() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { stopped = true; window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisible) }
  }, [lease])
  const fetchStorage = async () => {
    await renewal.current
    const response = await cloudFetch('/api/user/storage', { headers: headers(), cache: 'no-store' })
    if (!response.ok) throw new Error(response.status === 401 ? 'Tu sesión de archivos venció. Inicia sesión de nuevo.' : `No se pudo consultar tu almacenamiento (${response.status}).`)
    const data: Storage = await response.json()
    const personalParent = ['parent_guest', 'parent'].includes(data.role) && /^res_[a-f0-9]{40}$/.test(data.storage?.files?.resource_id || '')
    if (String((data as Storage & { email?: string }).email || '').toLowerCase() !== email.toLowerCase() || (!personalParent && !['guest', 'trusted_guest', 'external_guest', 'external_collaborator'].includes(data.role))) {
      setStorage(null)
      throw new Error('La sesión de archivos no reconoce tu cuenta Guest. Inicia sesión de nuevo para acceder a tu nube.')
    }
    const resource = data.storage.files?.resource_id || data.storage.workspace?.resource_id
    if (!resource) throw new Error('Tu carpeta personal no está disponible.')
    const listing = await cloudFetch(`/api/list?resource_id=${encodeURIComponent(resource)}`, { headers: headers(), cache: 'no-store' })
    if (!listing.ok) throw new Error('No se pudieron listar tus archivos.')
    const result = await listing.json()
    if (!Array.isArray(result.files)) throw new Error('El servidor no devolvió un listado válido. No se mostrará como una carpeta vacía.')
    setStorage(data)
    setFiles(result.files)
    setLoaded(true)
    setLastUpdated(new Date().toLocaleTimeString())
    return data
  }
  const refresh = () => {
    if (!refreshing.current) refreshing.current = fetchStorage().finally(() => { refreshing.current = null })
    return refreshing.current
  }
  const checkStorage = async () => {
    if (uploading.current) return
    setChecking(true)
    setStatus('Actualizando tu nube…')
    try { await refresh(); setStatus('Archivos actualizados.') }
    catch (error) { setStatus(error instanceof Error ? error.message : 'No se pudo actualizar.') }
    finally { setChecking(false) }
  }
  useEffect(() => {
    setStorage(null); setFiles([]); setLoaded(false); setLastUpdated(''); setViewer(null)
  }, [email])
  useEffect(() => {
    if (!active) { setViewer(null); return }
    const update = () => { if (document.visibilityState === 'visible' && !uploading.current) void checkStorage() }
    update()
    const timer = window.setInterval(update, 30000)
    document.addEventListener('visibilitychange', update)
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', update) }
  }, [active, lease, email])
  const upload = async (selected: FileList | null) => {
    if (!selected?.length || uploading.current) return
    const selectedFiles = Array.from(selected)
    uploading.current = true
    setBusy(true)
    setBatch(selectedFiles.map(file => ({ name: file.name, result: 'Pendiente' })))
    try {
      await renewal.current
      const currentStorage = storage || await refresh()
      const resource = currentStorage.storage.files?.resource_id || currentStorage.storage.workspace?.resource_id
      if (!resource) throw new Error('Tu carpeta personal no está disponible.')
      let completed = 0
      let duplicated = 0
      let failed = 0
      for (const [index, file] of selectedFiles.entries()) {
        setStatus(`Subiendo ${index + 1} de ${selectedFiles.length}: ${file.name}…`)
        const mark = (result: string) => setBatch(previous => previous.map((item, position) => position === index ? { ...item, result } : item))
        mark('Subiendo…')
        try {
        const form = new FormData()
        form.append('file', file)
        form.append('target_resource_id', resource)
        const response = await fetch('/api/upload', { method: 'POST', headers: headers(), body: form })
        if (response.status === 409) { duplicated++; mark('Ya existe'); continue }
        if (!response.ok) throw new Error(`Error ${response.status}`)
        const result = await response.json()
        if (result.ok === false) throw new Error('El servidor no confirmó la carga')
        completed++
        mark('Subido')
        } catch (error) { failed++; mark(error instanceof Error ? error.message : 'Falló la carga') }
      }
      const summary = `${completed} subidos · ${duplicated} ya existían · ${failed} fallaron.`
      try { await refresh(); setStatus(summary) }
      catch { setStatus(`${summary} No se pudo actualizar la lista; pulsa Actualizar.`) }
    } catch (error) { setStatus(error instanceof Error ? error.message : 'Error al subir.') }
    finally { uploading.current = false; setBusy(false) }
  }
  const deleteFile = async () => {
    if (!pendingDelete || busy) return
    const selected = pendingDelete
    setBusy(true)
    try {
      await renewal.current
      const response = await cloudFetch('/api/file/delete', {
        method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ resource_id: selected.resource_id })
      })
      if (!response.ok) throw new Error(`No se pudo eliminar (${response.status}).`)
      setPendingDelete(null)
      setFiles(previous => previous.filter(file => file.resource_id !== selected.resource_id))
      setStatus(`${selected.name} se movió a la papelera.`)
      try { await refresh() } catch { setStatus('Archivo enviado a papelera. Actualiza para consultar el consumo.') }
    } catch (error) { setStatus(error instanceof Error ? error.message : 'No se pudo eliminar.') }
    finally { setBusy(false) }
  }
  if (!storage && !status) return null
  return <section className="parent-notification-guide" aria-label="Mi nube">
    <h2>Mi nube</h2>
    <p style={{ overflowWrap: 'anywhere' }}>{email}</p>
    {lastUpdated && <small>Última sincronización: {lastUpdated}</small>}
    {storage && <><p>Espacio utilizado: {sizeLabel(storage.quota.used)} de {sizeLabel(storage.quota.bytes)}</p>
      <progress style={{ width: '100%' }} max={storage.quota.bytes || 1} value={storage.quota.used} aria-label="Almacenamiento utilizado" /></>}
    <div className="parent-header-actions">
      <label className="parent-primary-btn" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, overflow: 'hidden' }}><Upload size={18} /> {busy ? 'Subiendo…' : 'Subir archivos'}
        <input aria-label="Seleccionar fotos, videos o documentos" type="file" multiple disabled={busy || checking} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} onChange={event => { void upload(event.target.files); event.target.value = '' }} />
      </label>
      <button type="button" className="parent-secondary-btn" disabled={busy || checking} onClick={() => void checkStorage()}><RefreshCw size={18} /> {checking ? 'Actualizando…' : 'Actualizar'}</button>
    </div>
    <p role="status">{status}</p>
    {batch.length > 0 && <ul className="guest-cloud-files" aria-label="Resultado de la carga">{batch.map((item, index) => <li key={index}><span title={item.name}>{item.name}</span><small>{item.result}</small></li>)}</ul>}
    {loaded && <nav className="guest-cloud-categories" aria-label="Tipos de archivo">{fileCategories.map(item => <button type="button" key={item} aria-pressed={category === item} onClick={() => setCategory(item)}>{item} ({files.filter(file => !file.is_dir && (item === 'Todos' || fileCategory(file.name) === item)).length})</button>)}</nav>}
    {pendingDelete && <div role="alertdialog" aria-label="Confirmar eliminación"><p>¿Mover “{pendingDelete.name}” a la papelera?</p><div className="parent-header-actions"><button type="button" className="parent-secondary-btn" disabled={busy} onClick={() => setPendingDelete(null)}>Cancelar</button><button type="button" className="parent-primary-btn" disabled={busy} onClick={() => void deleteFile()}>Mover a papelera</button></div></div>}
    <ul className="guest-cloud-files">{files.filter(file => category === 'Todos' || (!file.is_dir && fileCategory(file.name) === category)).map(file => <li className="guest-cloud-entry" key={file.name}>{!file.is_dir && ['Fotos', 'Videos'].includes(fileCategory(file.name)) ? <button type="button" className="guest-cloud-open" title={file.name} aria-label={`Abrir ${file.name}`} onClick={() => setViewer({ items: files.filter(item => !item.is_dir && fileCategory(item.name) === fileCategory(file.name)), selectedId: file.resource_id })}>{file.name}</button> : <span title={file.name}>{file.name}</span>}<small>{file.is_dir ? 'Carpeta' : fileSizeLabel(file.size)}</small>{!file.is_dir && <button type="button" className="parent-secondary-btn" style={{ minWidth: 44, minHeight: 44, padding: 8 }} title="Eliminar archivo" aria-label={`Eliminar ${file.name}`} disabled={busy || checking || !file.resource_id} onClick={() => setPendingDelete(file)}><Trash2 size={18} /></button>}</li>)}</ul>
    {active && viewer && <GuestMediaViewer items={viewer.items} selectedId={viewer.selectedId} getHeaders={headers} onClose={() => setViewer(null)} />}
    {loaded && !files.some(file => category === 'Todos' || (!file.is_dir && fileCategory(file.name) === category)) && <p>No hay archivos en esta categoría.</p>}
  </section>
}
